import { Link, useParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import {
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../components/common';
import { stats } from '../../api/stats';
import { formatRelative } from '../../lib/format';
import { useHubLogin } from '../../session';
import { identityPath } from '../../identity/session';
import { EnvLinks } from './EnvLinks';
import { IncidentsTable } from './Incidents';
import { MonitoringTab } from './MonitoringTab';
import { SubscriptionsPanel } from './Reports';
import { authCustomerOf, derivedLocation, envsForOrg, useStatsLinks } from './links';
import { ErrorGroupsSection, PerformanceSection, VolumesSection } from './sections';
import { InstallationMarks } from './InstallationMarks';
import { IngestStatus, installationPath, statsError, useParam } from './shared';
import './statistics.css';

type Tab = 'overview' | 'performance' | 'volumes' | 'errors' | 'incidents' | 'monitoring' | 'reports';
const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'performance', label: 'Performance' },
  { value: 'volumes', label: 'Volumes & licences' },
  { value: 'errors', label: 'Errors' },
  { value: 'incidents', label: 'Incidents' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'reports', label: 'Reports' },
];

/** One organization (PMS customer id): installations, performance, volumes, errors, incidents, settings, reports. */
export function OrganizationPage() {
  const { customerId: raw } = useParams();
  const customerId = Number(raw);
  const login = useHubLogin();
  const canAdmin = hasScope(login?.scopes, SCOPES.statsAdmin);
  const [tab, setTab] = useParam<Tab>('tab', 'overview');
  const org = useAsync(() => stats.organization(customerId), [customerId]);
  const toast = useNotice();
  const links = useStatsLinks();
  const envs = envsForOrg(links, customerId);
  const derived = derivedLocation(envs);
  const customer = authCustomerOf(links, customerId);
  const o = org.data;
  if (!Number.isInteger(customerId) || customerId <= 0)
    return <EmptyState card title="Not an organization id." />;
  return (
    <Page
      title={o?.name || `Customer ${customerId}`}
      description={
        <span className="sx-links">
          <span>PMS customer id {customerId}</span>
          {o && <IngestStatus status={o.status} monitoring={o.active} />}
          {customer && links.target && (
            <Link className="nyk-link" to={identityPath('/identity/customers', links.target)}>
              AUTH customer {customer.name}
            </Link>
          )}
        </span>
      }
    >
      <ErrorBox error={org.error} />
      {toast.element}
      <Loading loading={org.loading && !o} />
      <Tabs value={tab} onChange={setTab} options={TABS} />
      {o && tab === 'overview' && (
        <section className="sx-body" data-testid="overview">
          <div className="nyk-card">
            <dl className="sx-meta">
              <span>
                Location <b>{o.location || derived?.value || '—'}</b>
                {o.location
                  ? derived && derived.value !== o.location
                    ? ` (override; derived ${derived.value})`
                    : ' (override)'
                  : derived
                    ? ' (derived)'
                    : ''}
              </span>
              <span>
                Last performance <b>{formatRelative(o.lastReceived.performance)}</b>
              </span>
              <span>
                Last log <b>{formatRelative(o.lastReceived.logs)}</b>
              </span>
              <span>
                Last daily metric <b>{formatRelative(o.lastReceived.dailyMetrics)}</b>
              </span>
              <span>
                Monitoring <b>{o.active ? 'on' : 'off'}</b> · {o.performance.length} tracked methods
              </span>
            </dl>
            <EnvLinks envs={envs} via="organization" links={links} />
          </div>
          <div className="nyk-card">
            <div className="nyk-card-head">
              <h2>Installations</h2>
              <span className="nyk-ui-hint">WorkServers that sent data</span>
            </div>
            {!o.installations.length ? (
              <EmptyState title="No installation has reported yet." />
            ) : (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="installations">
                  <thead>
                    <tr>
                      <th>WorkServer</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>Performance</th>
                      <th>Sys stats</th>
                      <th>Log</th>
                      <th>Daily metric</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.installations.map((i) => (
                      <tr key={i.workserver} data-ws={i.workserver}>
                        <td className="nyk-card-title">
                          <Link className="nyk-link" to={installationPath(customerId, i.workserver)}>
                            {i.workserver}
                          </Link>
                          {!!i.silentKinds.length && (
                            <div className="nyk-ui-hint">stale: {i.silentKinds.join(', ')}</div>
                          )}
                        </td>
                        <td data-label="Status">
                          <IngestStatus status={i.status} monitoring idle={i.idle} />
                          <InstallationMarks
                            inst={i}
                            canAdmin={canAdmin}
                            onChanged={() => void org.reload()}
                            onError={(e) => toast.error(statsError(e))}
                          />
                        </td>
                        <td data-label="Version">{i.version ?? '—'}</td>
                        <td data-label="Performance">{formatRelative(i.lastSeen.performance)}</td>
                        <td data-label="Sys stats">{formatRelative(i.lastSeen.sys)}</td>
                        <td data-label="Log">{formatRelative(i.lastSeen.log)}</td>
                        <td data-label="Daily metric">{formatRelative(i.lastSeen.metric)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </div>
        </section>
      )}
      {tab === 'performance' && <PerformanceSection customerId={customerId} />}
      {tab === 'volumes' && <VolumesSection customerId={customerId} />}
      {tab === 'errors' && <ErrorGroupsSection customerId={customerId} />}
      {tab === 'incidents' && <IncidentsTable customerId={customerId} compact />}
      {o && tab === 'monitoring' && (
        <MonitoringTab
          key={JSON.stringify([o.active, o.location, o.filters, o.performance, o.alarmSkipWorkserver])}
          org={o}
          canAdmin={canAdmin}
          derived={derived}
          onSaved={(saved) => org.setData(saved)}
          onTokenIssued={() => void org.reload()}
        />
      )}
      {tab === 'reports' && <SubscriptionsPanel customer={{ id: customerId, name: o?.name ?? null }} />}
    </Page>
  );
}
